import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Shield, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import * as OTPAuth from 'otpauth';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';

interface TwoFactorVerificationProps {
  userId: string;
  userEmail: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const TwoFactorVerification = ({ 
  userId, 
  userEmail, 
  onSuccess, 
  onCancel 
}: TwoFactorVerificationProps) => {
  const [code, setCode] = useState('');
  const [verifying, setVerifying] = useState(false);

  const handleVerify = async () => {
    if (code.length !== 6) return;

    setVerifying(true);
    try {
      // Use the security definer function to get TOTP secret (bypasses RLS)
      const { data: totpData, error: fetchError } = await supabase
        .rpc('get_totp_secret_for_verification', { p_user_id: userId });

      if (fetchError || !totpData || totpData.length === 0) {
        console.error('Error fetching TOTP settings:', fetchError);
        toast.error('Could not retrieve 2FA settings. Please try again.');
        setVerifying(false);
        return;
      }

      const secret = totpData[0].encrypted_secret;
      
      if (!secret) {
        toast.error('2FA is not properly configured.');
        setVerifying(false);
        return;
      }

      // Verify the code
      const totp = new OTPAuth.TOTP({
        issuer: 'MotoReveal',
        label: userEmail,
        algorithm: 'SHA1',
        digits: 6,
        period: 30,
        secret: OTPAuth.Secret.fromBase32(secret)
      });

      const delta = totp.validate({ token: code, window: 1 });
      
      if (delta === null) {
        toast.error('Invalid verification code. Please try again.');
        setCode('');
        setVerifying(false);
        return;
      }

      onSuccess();
    } catch (err) {
      console.error('Error verifying 2FA:', err);
      toast.error('Verification failed. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-primary/10 flex items-center justify-center">
          <Shield className="w-8 h-8 text-primary" />
        </div>
        <h2 className="text-2xl font-semibold text-foreground">Two-Factor Authentication</h2>
        <p className="text-muted-foreground mt-2">
          Enter the 6-digit code from your authenticator app
        </p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="2fa-code" className="sr-only">Verification Code</Label>
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={(value) => setCode(value)}
              onComplete={handleVerify}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>
        </div>

        <Button 
          onClick={handleVerify}
          disabled={verifying || code.length !== 6}
          className="w-full h-12 bg-foreground text-background hover:bg-foreground/90 rounded-xl"
        >
          {verifying ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Verifying...
            </>
          ) : (
            'Verify'
          )}
        </Button>

        <button
          type="button"
          onClick={onCancel}
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          Cancel and sign out
        </button>
      </div>
    </div>
  );
};
